import { useNavigate } from 'react-router-dom';
import { WIZARD_PATHS, wizardUrl } from './wizardRoutes';

// Eén stappenmodel voor de blog-wizard: bepaalt per stap de status (kleur),
// of de stap klikbaar is, en welk scherm een klik opent. De bolletjes
// (StepIndicator) en de klik (useWizardNavigation) kijken naar dezelfde data.
// Geef altijd de sessie door die het scherm zelf geladen heeft (activeSession),
// niet de SessionContext: die wordt in de gewone route nooit gevuld.

export type WizardStepStatus = 'completed' | 'current' | 'upcoming';

export interface WizardStep {
  number: number;
  label: string;
  // Keuzescherm van de stap. Voor Titel/Intro/Afbeelding: het select-scherm als
  // er al varianten zijn, anders het generatorscherm.
  path: string;
  status: WizardStepStatus;
  // true = klik opent `path`. Alleen voltooide stappen zijn ooit klikbaar.
  reachable: boolean;
  // Uitleg (tooltip) als de stap voltooid is maar niet klikbaar.
  lockedReason?: string;
}

// De velden van `sessions` die het stappenmodel gebruikt. Bewust los van het
// volledige Session-type: schermen geven vaak een `any` uit de database door.
export interface WizardSessionData {
  id?: string;
  state?: string | null;
  approved_idea_id?: string | null;
  article_content?: string | null;
  selected_title?: string | null;
  selected_intro?: string | null;
  selected_image_url?: string | null;
  titles?: unknown;
  intros?: unknown;
  images?: unknown;
}

export interface WizardStepOptions {
  // Waar stap 1 (Idee) heen wijst. Standaard de AI-ideeënlijst van de sessie;
  // ArticleDirection zet hier '/ideeen' als de sessie uit één bewaard idee komt.
  ideaStepPath?: string;
}

export const LOCKED_AFTER_WRITING = 'Vast na het schrijven van het artikel';
export const LOCKED_NO_DATA = 'Geen gegevens voor deze stap';

function hasItems(value: unknown): boolean {
  return Array.isArray(value) && value.length > 0;
}

export function isPreviewComplete(session: WizardSessionData | null | undefined): boolean {
  return !!(session?.selected_title && session?.selected_intro && session?.selected_image_url);
}

interface StepDef {
  number: number;
  label: string;
  path: string;
  done: boolean;
  reachable: boolean;
  lockedReason?: string;
}

function stepDefs(s: WizardSessionData, opts: WizardStepOptions): StepDef[] {
  const hasArticle = !!s.article_content;
  const hasTitles = hasItems(s.titles);
  const hasIntros = hasItems(s.intros);
  const hasImages = hasItems(s.images);
  return [
    {
      number: 1,
      label: 'Idee',
      path: opts.ideaStepPath ?? WIZARD_PATHS.ideaSelect,
      done: !!s.approved_idea_id || s.state === 'idea_approved',
      reachable: !hasArticle,
      lockedReason: LOCKED_AFTER_WRITING,
    },
    {
      number: 2,
      label: 'Richting',
      path: WIZARD_PATHS.direction,
      done: hasArticle,
      reachable: true,
    },
    {
      number: 3,
      label: 'Artikel',
      path: WIZARD_PATHS.articleEdit,
      done: hasArticle,
      reachable: hasArticle,
    },
    {
      number: 4,
      label: 'Titel',
      path: hasTitles ? WIZARD_PATHS.selectTitle : WIZARD_PATHS.titleGenerate,
      done: !!s.selected_title,
      reachable: hasTitles || !!s.selected_title,
    },
    {
      number: 5,
      label: 'Intro',
      path: hasIntros ? WIZARD_PATHS.selectIntro : WIZARD_PATHS.introGenerate,
      done: !!s.selected_intro,
      reachable: hasIntros || !!s.selected_intro,
    },
    {
      number: 6,
      label: 'Afbeelding',
      path: hasImages ? WIZARD_PATHS.selectImage : WIZARD_PATHS.imageGenerate,
      done: !!s.selected_image_url,
      reachable: hasImages || !!s.selected_image_url,
    },
    {
      number: 7,
      label: 'Preview',
      path: WIZARD_PATHS.preview,
      done: isPreviewComplete(s),
      reachable: isPreviewComplete(s),
    },
  ];
}

export function getWizardSteps(
  session: WizardSessionData | null | undefined,
  currentStepNumber: number,
  opts: WizardStepOptions = {},
): WizardStep[] {
  const defs = stepDefs(session ?? {}, opts);
  // Voltooid is monotoon: alles vóór de verste voltooide stap telt ook als voltooid
  // (een geïmporteerde tekst heeft bijvoorbeeld geen idee, maar wel een artikel).
  const lastDone = defs.reduce((max, d) => (d.done ? Math.max(max, d.number) : max), 0);
  return defs.map((d) => {
    const status: WizardStepStatus =
      d.number === currentStepNumber ? 'current' : d.number <= lastDone ? 'completed' : 'upcoming';
    const reachable = status === 'completed' && d.reachable;
    const lockedReason =
      status === 'completed' && !reachable ? (d.lockedReason ?? LOCKED_NO_DATA) : undefined;
    return { number: d.number, label: d.label, path: d.path, status, reachable, lockedReason };
  });
}

// Eerste stap na het artikel die nog een keuze mist, anders de preview.
export function nextIncompleteRoute(session: WizardSessionData): string {
  if (!session.selected_title) {
    return hasItems(session.titles) ? WIZARD_PATHS.selectTitle : WIZARD_PATHS.titleGenerate;
  }
  if (!session.selected_intro) {
    return hasItems(session.intros) ? WIZARD_PATHS.selectIntro : WIZARD_PATHS.introGenerate;
  }
  if (!session.selected_image_url) {
    return hasItems(session.images) ? WIZARD_PATHS.selectImage : WIZARD_PATHS.imageGenerate;
  }
  return WIZARD_PATHS.preview;
}

// URL die een klik op een bolletje opent, of null als de klik niets mag doen
// (stap niet bereikbaar, of geen sessie-id). Puur, zodat het testbaar is.
export function stepClickUrl(step: WizardStep, sessionId: string | null | undefined): string | null {
  if (!step.reachable || !sessionId) return null;
  return wizardUrl(step.path, sessionId);
}

// Klik op een bolletje: open het keuzescherm van die stap met het sessie-id in de URL.
export function useWizardNavigation(session: WizardSessionData | null | undefined) {
  const navigate = useNavigate();

  const handleStepClick = (step: WizardStep) => {
    const url = stepClickUrl(step, session?.id);
    if (url) navigate(url);
  };

  return { handleStepClick };
}
